import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { InlineAddressCell } from '@/components/ui/table-cells/InlineAddressCell';
import { InlineAssigneesCell } from '@/components/ui/table-cells/InlineAssigneesCell';
import { InlineCompanyCell } from '@/components/ui/table-cells/InlineCompanyCell';
import { InlineContactCell } from '@/components/ui/table-cells/InlineContactCell';
import { InlineDateCell } from '@/components/ui/table-cells/InlineDateCell';
import { InlinePriorityCell } from '@/components/ui/table-cells/InlinePriorityCell';
import { InlineStatusCell } from '@/components/ui/table-cells/InlineStatusCell';
import { InlineTypeCell } from '@/components/ui/table-cells/InlineTypeCell';
import { TableColumnHeader } from '@/components/ui/table-cells/TableColumnHeader';
import { useTranslations } from '@/hooks/useTranslations';
import { cn, formatDate } from '@/lib/utils';
import { type User, type WorkOrder, type WorkOrderAssignment } from '@/types';
import { Link, router } from '@inertiajs/react';
import { differenceInDays } from 'date-fns';

interface WorkOrderTableProps {
    workOrders: WorkOrder[];
    users?: User[];
    onUpdate?: {
        estimatedDate?: (workOrderId: string, value: string | null) => Promise<void>;
        priority?: (workOrderId: string, value: string) => Promise<void>;
        status?: (workOrderId: string, value: string) => Promise<void>;
        type?: (workOrderId: string, value: string) => Promise<void>;
        address?: (workOrderId: string, value: string, addressData?: { lat?: number; lng?: number }) => Promise<void>;
        contact?: (
            workOrderId: string,
            data: {
                contact_first_name: string;
                contact_last_name: string;
                phone?: string;
                email?: string;
                service_address: string;
            },
        ) => Promise<void>;
        assignedUser?: (workOrderId: string, userId: string | null) => Promise<void>;
    };
    sortColumn?: string | null;
    sortDirection?: 'asc' | 'desc' | null;
    onSortChange?: (column: string, direction: 'asc' | 'desc') => void;
    onColumnHide?: (column: string) => void;
    onColumnShow?: (column: string) => void;
    visibleColumns?: Set<string>;
    columnFilters?: Record<string, string[]>;
    columnFilterOptions?: Record<string, Array<{ value: string; label: string }>>;
    onFilterChange?: (column: string, values: string[]) => void;
    statuses?: Array<{ value: string; label: string; color: string; isTerminal?: boolean }>;
    priorities?: Array<{ value: string; label: string; color: string; icon?: string }>;
    types?: Array<{ value: string; label: string; color: string; icon?: string }>;
    readOnly?: boolean;
}

const DEFAULT_COLUMNS = [
    'work_order',
    'contact',
    'service_address',
    'company',
    'assigned_to',
    'type',
    'status',
    'priority',
    'estimated_date',
    'created_at',
];

export function WorkOrderTable({
    workOrders,
    users = [], // eslint-disable-line @typescript-eslint/no-unused-vars
    onUpdate,
    sortColumn,
    sortDirection,
    onSortChange,
    onColumnHide,
    visibleColumns = new Set(DEFAULT_COLUMNS),
    columnFilters = {},
    columnFilterOptions = {},
    onFilterChange,
    statuses,
    priorities,
    types,
    readOnly = false,
}: WorkOrderTableProps) {
    const { t } = useTranslations();
    const translatedStatuses = statuses?.map((status) => ({
        ...status,
        label: t(`settings.work_order_statuses.${status.value}`, status.label),
    }));
    const translatedPriorities = priorities?.map((priority) => ({
        ...priority,
        label: t(`settings.work_order_priorities.${priority.value}`, priority.label),
    }));
    const translatedTypes = types?.map((type) => {
        const translationKeyValue = type.value.trim().toLowerCase().replace(/\s+/g, '_');

        return {
            ...type,
            label: t(`settings.work_order_types.${translationKeyValue}`, type.label),
        };
    });

    const handleRowClick = (workOrderId: string) => {
        router.visit(`/work-orders/${workOrderId}`);
    };

    const handleSort = (column: string, direction: 'asc' | 'desc') => {
        onSortChange?.(column, direction);
    };

    const handleColumnHide = (column: string) => {
        onColumnHide?.(column);
    };

    const allColumns = [
        {
            id: 'work_order',
            label: t('work_orders.index.columns.work_order', 'Work Order'),
            visible: visibleColumns.has('work_order'),
        },
        {
            id: 'contact',
            label: t('work_orders.index.columns.contact', 'Contact'),
            visible: visibleColumns.has('contact'),
        },
        {
            id: 'service_address',
            label: t('work_orders.index.columns.service_address', 'Service Address'),
            visible: visibleColumns.has('service_address'),
        },
        {
            id: 'company',
            label: t('work_orders.index.columns.company', 'Company'),
            visible: visibleColumns.has('company'),
        },
        {
            id: 'assigned_to',
            label: t('work_orders.index.columns.assigned_to', 'Assigned To'),
            visible: visibleColumns.has('assigned_to'),
        },
        {
            id: 'type',
            label: t('work_orders.index.columns.type', 'Type'),
            visible: visibleColumns.has('type'),
        },
        {
            id: 'status',
            label: t('work_orders.index.columns.status', 'Status'),
            visible: visibleColumns.has('status'),
        },
        {
            id: 'priority',
            label: t('work_orders.index.columns.priority', 'Priority'),
            visible: visibleColumns.has('priority'),
        },
        {
            id: 'estimated_date',
            label: t('work_orders.index.columns.estimated_date', 'Estimated Date'),
            visible: visibleColumns.has('estimated_date'),
        },
        {
            id: 'created_at',
            label: t('work_orders.index.columns.created_at', 'Created At'),
            visible: visibleColumns.has('created_at'),
        },
        {
            id: 'created_by',
            label: t('work_orders.index.columns.created_by', 'Created By'),
            visible: visibleColumns.has('created_by'),
        },
    ];

    const columns = allColumns.filter((col) => col.visible);

    // Helper function to check if work order is overdue
    const isWorkOrderOverdue = (workOrder: WorkOrder): boolean => {
        const statusKey = (workOrder.status ?? '').toLowerCase();
        const isCompleted = statusKey === 'completed';
        const isCancelled = statusKey === 'canceled' || statusKey === 'cancelled';

        // Don't show overdue for completed/cancelled work orders
        if (isCompleted || isCancelled) return false;

        // Check if estimated date exists and is in the past
        if (!workOrder.estimated_date) return false;

        const estimatedDate = new Date(workOrder.estimated_date);
        if (Number.isNaN(estimatedDate.getTime())) return false;

        estimatedDate.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const daysDiff = differenceInDays(estimatedDate, today);
        return daysDiff < 0; // Overdue if estimated date is in the past
    };

    return (
        <div className="space-y-4">
            <div className="bg-card mb-4 items-center justify-between gap-3 rounded-xl border px-4 py-2.5">
                <Table>
                    <TableHeader>
                        <TableRow>
                            {columns.map((column) => {
                                const isSorted = sortColumn === column.id;
                                const currentSortDirection = isSorted ? sortDirection : null;
                                const selectedFilters = columnFilters[column.id] || [];

                                return (
                                    <TableHead key={column.id}>
                                        <TableColumnHeader
                                            label={column.label}
                                            sortDirection={currentSortDirection}
                                            onSortAscending={() => handleSort(column.id, 'asc')}
                                            onSortDescending={() => handleSort(column.id, 'desc')}
                                            onHide={() => handleColumnHide(column.id)}
                                            filterOptions={columnFilterOptions[column.id] || []}
                                            selectedFilters={selectedFilters}
                                            onFilterChange={(values) => onFilterChange?.(column.id, values)}
                                            isFiltered={selectedFilters.length > 0}
                                        />
                                    </TableHead>
                                );
                            })}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {workOrders.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="text-muted-foreground py-8 text-center">
                                    {t('work_orders.tables.no_work_orders_found', 'No work orders found.')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            workOrders.map((workOrder) => {
                                const assignedUser = workOrder.assigned_user;
                                const assignments: WorkOrderAssignment[] | undefined = workOrder.assignments;
                                const isOverdue = isWorkOrderOverdue(workOrder);

                                return (
                                    <TableRow
                                        key={workOrder.id}
                                        className={cn(
                                            'cursor-pointer transition-all',
                                            isOverdue
                                                ? 'border-l-destructive/60 bg-destructive/5 hover:bg-destructive/10 hover:border-l-destructive/80 border-l-4'
                                                : 'hover:bg-muted/50',
                                        )}
                                        onClick={() => handleRowClick(workOrder.id)}
                                    >
                                        {columns.map((column) => {
                                            if (column.id === 'work_order') {
                                                return (
                                                    <TableCell key={column.id}>
                                                        <Link
                                                            href={`/work-orders/${workOrder.id}`}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="hover:bg-accent text-foreground w-full rounded px-2 py-1.5 text-left font-semibold transition-colors"
                                                        >
                                                            {workOrder.wo_number || `WO-${workOrder.id.slice(0, 8)}`}
                                                        </Link>
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'contact') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        <InlineContactCell
                                                            workOrder={workOrder}
                                                            onUpdate={
                                                                !readOnly && onUpdate?.contact
                                                                    ? async (data) => {
                                                                          await onUpdate.contact!(workOrder.id, data);
                                                                      }
                                                                    : undefined
                                                            }
                                                            readOnly={readOnly}
                                                        />
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'service_address') {
                                                // Build full address from separate fields
                                                const addressParts = [
                                                    workOrder.service_address_street,
                                                    workOrder.service_address_house_number,
                                                    workOrder.service_address_city,
                                                    workOrder.service_address_postal_code,
                                                ].filter((part): part is string => Boolean(part));
                                                const fullAddress =
                                                    addressParts.length > 0 ? addressParts.join(', ') : workOrder.service_address || null;

                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        <InlineAddressCell
                                                            value={fullAddress}
                                                            onUpdate={
                                                                !readOnly && onUpdate?.address
                                                                    ? async (value, addressData) => {
                                                                          await onUpdate.address!(workOrder.id, value, addressData);
                                                                      }
                                                                    : undefined
                                                            }
                                                            placeholder="Enter address..."
                                                            readOnly={readOnly}
                                                        />
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'company') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        <InlineCompanyCell company={workOrder.company} onUpdate={undefined} />
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'assigned_to') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        <InlineAssigneesCell assignments={assignments} assignedUser={assignedUser} />
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'status') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        <InlineStatusCell
                                                            value={workOrder.status}
                                                            onUpdate={
                                                                !readOnly && onUpdate?.status
                                                                    ? async (value) => {
                                                                          await onUpdate.status!(workOrder.id, value);
                                                                      }
                                                                    : undefined
                                                            }
                                                            statuses={translatedStatuses}
                                                            readOnly={readOnly}
                                                        />
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'type') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        <InlineTypeCell
                                                            value={workOrder.work_type as string | null | undefined}
                                                            onUpdate={
                                                                !readOnly && onUpdate?.type
                                                                    ? async (value) => {
                                                                          await onUpdate.type!(workOrder.id, value);
                                                                      }
                                                                    : undefined
                                                            }
                                                            types={translatedTypes}
                                                            readOnly={readOnly}
                                                        />
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'priority') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        <InlinePriorityCell
                                                            value={workOrder.priority || null}
                                                            onUpdate={
                                                                !readOnly && onUpdate?.priority
                                                                    ? async (value) => {
                                                                          await onUpdate.priority!(workOrder.id, value);
                                                                      }
                                                                    : undefined
                                                            }
                                                            priorities={translatedPriorities}
                                                            readOnly={readOnly}
                                                        />
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'estimated_date') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        <InlineDateCell
                                                            value={workOrder.estimated_date || null}
                                                            status={workOrder.status}
                                                            completedAt={workOrder.completed_at || null}
                                                            updatedAt={workOrder.updated_at || null}
                                                            onUpdate={
                                                                !readOnly && onUpdate?.estimatedDate
                                                                    ? async (value) => {
                                                                          await onUpdate.estimatedDate!(workOrder.id, value);
                                                                      }
                                                                    : undefined
                                                            }
                                                            readOnly={readOnly}
                                                        />
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'created_at') {
                                                return (
                                                    <TableCell key={column.id} className="text-muted-foreground text-sm whitespace-nowrap">
                                                        {workOrder.created_at ? formatDate(workOrder.created_at) : '—'}
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'created_by') {
                                                const creator = workOrder.created_by;
                                                const name = creator ? `${creator.first_name} ${creator.last_name}`.trim() : '—';
                                                return (
                                                    <TableCell key={column.id} className="text-sm whitespace-nowrap">
                                                        {name}
                                                    </TableCell>
                                                );
                                            }

                                            return null;
                                        })}
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
                {/* <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-2 border-t bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
                {columns.map((column) => {
                    const aggregation = columnAggregations[column.id] || 'count_filled';
                    const value = computeAggregation(column.id, aggregation);
                    const currentLabel = aggregationOptions.find((opt) => opt.id === aggregation)?.label || 'Count filled';

                    return (
                        <div key={column.id} className="flex items-center justify-between gap-2 rounded-md border bg-background px-3 py-2">
                            <div className="flex flex-col">
                                <span className="text-xs text-muted-foreground">{column.label}</span>
                                <span className="font-semibold text-foreground">{value}</span>
                            </div>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" className="gap-1">
                                        {currentLabel}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-44">
                                    <DropdownMenuLabel>Aggregations</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    {aggregationOptions.map((option) => (
                                        <DropdownMenuCheckboxItem
                                            key={`${column.id}-${option.id}`}
                                            checked={aggregation === option.id}
                                            onCheckedChange={() =>
                                                setColumnAggregations((prev: Record<string, AggregationType>) => ({
                                                    ...prev,
                                                    [column.id]: option.id,
                                                }))
                                            }
                                        >
                                            {option.label}
                                        </DropdownMenuCheckboxItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    );
                })}
            </div> */}
            </div>
        </div>
    );
}
